import React, {useState, useEffect, useRef} from 'react';
import {ChevronDown, Grid3x3, List, Download, Info, Loader2} from 'lucide-react';
import {HEALTH_CATEGORIES} from '../../utils/healthCategories';

interface LabResultsHeaderProps {
    onFilterChange?: (filter: string) => void;
    onSortChange?: (sort: string) => void;
    onViewChange?: (view: 'panel' | 'biomarker') => void;
    onDownload?: () => void;
    downloading?: boolean;
    hasAboutTest?: boolean;
    onAboutTest?: () => void;
    currentFilter?: string;
    currentSort?: string;
    showControls?: boolean;
    availableCategories?: string[]; // Health categories that have results
}

const LabResultsHeader: React.FC<LabResultsHeaderProps> = ({
    onFilterChange,
    onSortChange,
    onViewChange,
    onDownload,
    downloading = false,
    hasAboutTest = false,
    onAboutTest,
    currentFilter = 'all',
    currentSort = 'newest',
    showControls = true,
    availableCategories,
}) => {
    const [activeView, setActiveView] = useState<'panel' | 'biomarker'>('panel');
    const [filterOpen, setFilterOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setFilterOpen(false);
                setSortOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const handleViewChange = (view: 'panel' | 'biomarker') => {
        setActiveView(view);
        onViewChange?.(view);
    };

    // Get display text for current filter
    const getFilterDisplayText = () => {
        switch (currentFilter) {
            case 'all':
                return 'All Results';
            case 'normal':
            case 'within-range':
                return 'Within Range';
            case 'abnormal':
                return 'Any Abnormal';
            case 'borderline':
            case 'needs-attention':
                return 'Needs Attention';
            case 'critical':
            case 'high-priority':
                return 'High Priority';
            default:
                // Check if it's a health category
                if (currentFilter in HEALTH_CATEGORIES) {
                    return HEALTH_CATEGORIES[currentFilter].name;
                }
                return 'Filter By';
        }
    };

    // Get display text for current sort
    const getSortDisplayText = () => {
        switch (currentSort) {
            case 'alphabetical':
                return 'Alphabetical';
            case 'status':
                return 'By Status';
            default:
                return 'Sort By';
        }
    };

    return (
        <div className="py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">Your Lab Results</h1>

                <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                    {/* Download PDF Button */}
                    {onDownload && (
                        <button
                            onClick={onDownload}
                            className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mld-primary disabled:opacity-50"
                            disabled={downloading}>
                            {downloading ? (
                                <Loader2 className="h-3 w-3 lg:h-4 lg:w-4 animate-spin" />
                            ) : (
                                <Download className="h-3 w-3 lg:h-4 lg:w-4" />
                            )}
                            <span className="hidden sm:inline">{downloading ? 'Downloading...' : 'Download PDF'}</span>
                            <span className="sm:hidden">PDF</span>
                        </button>
                    )}

                    {/* About Test Button */}
                    {hasAboutTest && onAboutTest && (
                        <button
                            onClick={onAboutTest}
                            className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mld-primary">
                            <Info className="h-3 w-3 lg:h-4 lg:w-4" />
                            <span className="hidden sm:inline">About this test</span>
                            <span className="sm:hidden">About</span>
                        </button>
                    )}

                    {/* Show filter/sort/view controls only for panel tests */}
                    {showControls && (
                        <div ref={dropdownRef} className="flex flex-wrap items-center gap-2 lg:gap-3">
                            {/* Filter Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setSortOpen(false);
                                        setFilterOpen(!filterOpen);
                                    }}
                                    className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mld-primary">
                                    <span className="hidden sm:inline">{getFilterDisplayText()}</span>
                                    <span className="sm:hidden">Filter</span>
                                    <ChevronDown className="h-3 w-3 lg:h-4 lg:w-4" />
                                </button>

                                {filterOpen && (
                                    <div className="absolute right-0 sm:right-0 left-0 sm:left-auto mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 max-h-96 overflow-y-auto">
                                        <div className="py-1" role="menu">
                                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                Range
                                            </div>
                                            <button
                                                onClick={() => {
                                                    onFilterChange?.('all');
                                                    setFilterOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                All Results
                                            </button>
                                            <button
                                                onClick={() => {
                                                    onFilterChange?.('normal');
                                                    setFilterOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                Within Range
                                            </button>
                                            <button
                                                onClick={() => {
                                                    onFilterChange?.('borderline');
                                                    setFilterOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                Needs Attention
                                            </button>
                                            <button
                                                onClick={() => {
                                                    onFilterChange?.('critical');
                                                    setFilterOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                High Priority
                                            </button>
                                            <button
                                                onClick={() => {
                                                    onFilterChange?.('abnormal');
                                                    setFilterOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                Any Abnormal
                                            </button>

                                            {/* Only show Health Category section if there are available categories */}
                                            {(!availableCategories || availableCategories.length > 0) && (
                                                <>
                                                    <hr className="my-2" />
                                                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                        Health Category
                                                    </div>
                                                    {/* Render health categories from dashboard (excluding 'other'), filtered by availability */}
                                                    {Object.entries(HEALTH_CATEGORIES)
                                                        .filter(([key]) => key !== 'other')
                                                        .filter(
                                                            ([key]) =>
                                                                !availableCategories ||
                                                                availableCategories.includes(key)
                                                        )
                                                        .map(([key, category]) => (
                                                            <button
                                                                key={key}
                                                                onClick={() => {
                                                                    onFilterChange?.(key);
                                                                    setFilterOpen(false);
                                                                }}
                                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                                {category.name}
                                                            </button>
                                                        ))}
                                                    {/* Other category at the end - only show if available */}
                                                    {(!availableCategories ||
                                                        availableCategories.includes('other')) && (
                                                        <button
                                                            onClick={() => {
                                                                onFilterChange?.('other');
                                                                setFilterOpen(false);
                                                            }}
                                                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                            Other
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Sort Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => {
                                        setFilterOpen(false);
                                        setSortOpen(!sortOpen);
                                    }}
                                    className="flex items-center space-x-1 lg:space-x-2 px-3 lg:px-4 py-2 text-xs lg:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mld-primary">
                                    <span className="hidden sm:inline">{getSortDisplayText()}</span>
                                    <span className="sm:hidden">Sort</span>
                                    <ChevronDown className="h-3 w-3 lg:h-4 lg:w-4" />
                                </button>

                                {sortOpen && (
                                    <div className="absolute right-0 sm:right-0 left-0 sm:left-auto mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                                        <div className="py-1" role="menu">
                                            <button
                                                onClick={() => {
                                                    onSortChange?.('alphabetical');
                                                    setSortOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                Alphabetical (A-Z)
                                            </button>
                                            <button
                                                onClick={() => {
                                                    onSortChange?.('status');
                                                    setSortOpen(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                                By Status (Critical First)
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* View Toggle */}
                            <div className="flex items-center bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => handleViewChange('panel')}
                                    className={`flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-1.5 rounded-md text-xs lg:text-sm font-medium transition-colors ${
                                        activeView === 'panel'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}>
                                    <Grid3x3 className="h-3 w-3 lg:h-4 lg:w-4" />
                                    <span className="hidden lg:inline">Panel View</span>
                                    <span className="lg:hidden">Panel</span>
                                </button>
                                <button
                                    onClick={() => handleViewChange('biomarker')}
                                    className={`flex items-center space-x-1 lg:space-x-2 px-2 lg:px-3 py-1.5 rounded-md text-xs lg:text-sm font-medium transition-colors ${
                                        activeView === 'biomarker'
                                            ? 'bg-white text-gray-900 shadow-sm'
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}>
                                    <List className="h-3 w-3 lg:h-4 lg:w-4" />
                                    <span className="hidden lg:inline">Biomarker View</span>
                                    <span className="lg:hidden">List</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(LabResultsHeader);
